import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { styled } from '@material-ui/core/styles';
import { colors } from '../../infrastructure/newDesignTheme';
import { Position } from './nkDropdown.types';

interface DropdownProps {
  position: Position;
  menu: ReactNode;
  children: ReactNode;
  menuIndent?: number;
  onToggle?: (isOpen: boolean) => void;
  isOpen?: boolean;
}

export const NkDropdown = ({
  position,
  menu,
  children,
  menuIndent,
  onToggle,
  isOpen = false,
}: DropdownProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(isOpen);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => {
    setIsDropdownOpen((prevIsOpen) => !prevIsOpen);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    if (onToggle) {
      onToggle(isDropdownOpen);
    }
  }, [isDropdownOpen, onToggle]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <StyledDropdown ref={dropdownRef}>
      <div onClick={toggleDropdown}>{children}</div>
      {isDropdownOpen && (
        <StyledDropdownContent $position={position} $menuIndent={menuIndent}>
          {menu}
        </StyledDropdownContent>
      )}
    </StyledDropdown>
  );
};

const StyledDropdown = styled('div')({
  position: 'relative',
  display: 'inline-block',
});

interface StyledDropdownContentProps {
  $position: DropdownProps['position'];
  $menuIndent: DropdownProps['menuIndent'];
}

const getMenuIndentIndent = (position: Position, menuIndent?: number) => {
  if (menuIndent !== undefined) {
    if (position.startsWith('top')) {
      return {
        marginBottom: `${menuIndent}px`,
      };
    }
    if (position.startsWith('bottom')) {
      return {
        marginTop: `${menuIndent}px`,
      };
    }
  }
  return {};
};

const StyledDropdownContent = styled('div')(
  ({ $position, $menuIndent }: StyledDropdownContentProps) => ({
    position: 'absolute',
    borderRadius: '4px',
    zIndex: 1,
    // TODO: change this to appropriate theme color once theming is done on app level
    backgroundColor: colors.almostHuman,
    boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
    top: $position.startsWith('top') ? 'auto' : '100%',
    bottom: $position.startsWith('bottom') ? 'auto' : '100%',
    left: $position.endsWith('Left') ? 0 : 'auto',
    right: $position.endsWith('Right') ? 0 : 'auto',
    ...getMenuIndentIndent($position, $menuIndent),
  }),
);
